import { useEffect, useState } from 'react';
import { clientDirectoryApi, type ClientRecord } from './client-directory-api';
import { remunerationMoney } from './RemunerationColumn';
import { PrototypeIcon } from './PrototypeIcon';
import { FinancialCalculationStatus } from './FinancialCalculationStatus';

/** Cartes validées : structure commune en réel et démo ; montants fictifs isolés. */
export function ProvisionProposalCard({
  demo = true,
}: {
  readonly demo?: boolean;
}): React.JSX.Element {
  const [month, setMonth] = useState('2026-10');
  const rows = [
    {
      name: 'Urssaf & impôt du mois précédent',
      planned: 190000 + Number(month.slice(5)) * 1000,
      paid: 190000,
    },
    { name: `TVA du trimestre ${Math.ceil(Number(month.slice(5)) / 3)}`, planned: 60000, paid: 0 },
    { name: 'Dépenses fixes prévues', planned: 85000, paid: 35000 },
  ];
  const remaining = rows.reduce((sum, row) => sum + Math.max(0, row.planned - row.paid), 0);
  return (
    <article className="provision-proposal finance-card--controls">
      <header className="finance-card__heading">
        <h3 className="finance-card__title">
          <PrototypeIcon name="wallet" />À garder de côté
        </h3>
        <div className="finance-card__controls">
          <select
            aria-label="Mois des provisions proposées"
            value={month}
            onChange={(event) => setMonth(event.currentTarget.value)}
          >
            {Array.from({ length: 12 }, (_, i) => `2026-${String(i + 1).padStart(2, '0')}`).map(
              (value) => (
                <option key={value} value={value}>
                  {new Intl.DateTimeFormat('fr-FR', { month: 'long', timeZone: 'UTC' }).format(
                    new Date(`${value}-01T12:00:00Z`),
                  )}
                </option>
              ),
            )}
          </select>
        </div>
      </header>
      <p>
        Une préconisation pour le mois choisi : garder de côté les sommes attendues d’après les
        recettes déjà encaissées et les dépenses fixes. En début de mois, la part liée aux recettes
        peut être nulle ; les charges du mois précédent et les frais fixes restent à prévoir.
      </p>
      <div className="provision-proposal__total">
        <span>Reste à garder ce mois-ci</span>
        <strong>
          {demo ? (
            remunerationMoney(String(remaining))
          ) : (
            <FinancialCalculationStatus calculation="provisions" />
          )}
        </strong>
      </div>
      <table>
        <thead>
          <tr>
            <th>Pour quoi ?</th>
            <th>À prévoir</th>
            <th>Déjà payé</th>
            <th>À garder</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name}>
              <th scope="row">{row.name}</th>
              <td>{demo ? remunerationMoney(String(row.planned)) : 'À déterminer'}</td>
              <td>{demo ? remunerationMoney(String(row.paid)) : 'À déterminer'}</td>
              <td>
                {demo
                  ? remunerationMoney(String(Math.max(0, row.planned - row.paid)))
                  : 'À déterminer'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="provision-proposal__reserve">
        <span>Réserve de sécurité à compléter</span>
        <b>{demo ? '300 €' : 'À déterminer'}</b>
      </p>
      <p>
        Votre réserve est séparée des factures à payer. Vous ne la remettez pas entièrement de côté
        chaque mois.
      </p>
      <h4>Les prochaines dates</h4>
      <ul>
        <li>
          <span>Urssaf & impôt</span>
          <b>{demo ? 'Fin du mois' : 'Échéance à vérifier'}</b>
        </li>
        <li>
          <span>TVA</span>
          <b>Trimestrielle · échéance de votre déclaration</b>
        </li>
      </ul>
      <p className="provision-proposal__point">
        Le repère de CA configuré ne remplace pas le calcul des dépenses et des obligations restant
        à régler, ni celui de la réserve à compléter.
      </p>
      <small>
        {demo
          ? 'Données démo : montants et échéances fictifs.'
          : 'Données réelles : calculs et échéances en attente de validation ; aucun montant fictif affiché.'}
      </small>
    </article>
  );
}

export function ProfitabilityProposalCard({
  demo = true,
}: {
  readonly demo?: boolean;
}): React.JSX.Element {
  const [year, setYear] = useState('2026');
  const [scope, setScope] = useState('clients');
  const [clients, setClients] = useState<readonly ClientRecord[]>([]);
  const [directoryStatus, setDirectoryStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    let mounted = true;
    // Les noms et logos viennent du répertoire Tosho ; jamais d'exemples inventés.
    // Le résultat est tenant-scopé par l'API Clients existante.
    void Promise.all([
      clientDirectoryApi.search('ASRL'),
      clientDirectoryApi.search('Il était une frite'),
      clientDirectoryApi.search('', 0, 'clients'),
    ]).then(
      (directories) => {
        if (!mounted) return;
        const selected = new Map<string, ClientRecord>();
        for (const directory of directories) {
          for (const client of directory.clients) {
            if (client.stage === 'CLIENT' && !selected.has(client.id))
              selected.set(client.id, client);
            if (selected.size >= 4) break;
          }
        }
        setClients([...selected.values()].slice(0, 4));
        setDirectoryStatus('ready');
      },
      () => {
        if (mounted) setDirectoryStatus('error');
      },
    );
    return () => {
      mounted = false;
    };
  }, []);
  // Les chiffres restent des exemples de présentation, jamais des finances attribuées à ces clients.
  const examples = [
    { revenue: 1200000 + (Number(year) - 2020) * 20000, cost: 420000 },
    { revenue: 840000, cost: 360000 },
    { revenue: 590000, cost: 410000 },
    { revenue: 240000, cost: 290000 },
  ];
  const displayedClients = demo
    ? clients
    : [...clients].sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'));
  const rows = displayedClients.map((client, index) => ({
    client,
    ...examples[index]!,
  }));
  const maximum = Math.max(1, ...rows.map((row) => Math.abs(row.revenue - row.cost)));
  return (
    <article className="profitability-proposal finance-card--controls">
      <header className="finance-card__heading">
        <h3 className="finance-card__title">
          <PrototypeIcon name="piggy-bank" />
          Ce qui vous rapporte
        </h3>
        <div className="finance-card__controls">
          <select
            aria-label="Année de rentabilité proposée"
            value={year}
            onChange={(event) => setYear(event.currentTarget.value)}
          >
            {Array.from({ length: 7 }, (_, i) => 2026 - i).map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </div>
      </header>
      <div
        className="profitability-proposal__scope"
        role="group"
        aria-label="Comparer les clients ou les projets"
      >
        {['clients', 'projets'].map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={scope === value}
            onClick={() => setScope(value)}
          >
            {value === 'clients' ? 'Clients' : 'Projets'}
          </button>
        ))}
      </div>
      <p>
        Les recettes, les coûts rattachés et ce qu’il reste. Les coûts communs et votre temps
        devront aussi être pris en compte pour une rentabilité complète.
      </p>
      <p className="profitability-proposal__disclaimer">
        {demo
          ? 'Montants HT fictifs pour la démonstration ; seul le nom ou le logo du client provient de Tosho.'
          : 'Clients et logos réels Tosho. Recettes, coûts et marges non calculables avant la revue des sources : aucun chiffre fictif présenté.'}
      </p>
      {directoryStatus === 'loading' ? <p role="status">Chargement des clients Tosho…</p> : null}
      {directoryStatus === 'error' ? (
        <p role="status" className="tosho-feedback is-error">
          Impossible de charger l’annuaire Clients.
        </p>
      ) : null}
      {directoryStatus === 'ready' && !clients.length ? (
        <p>Aucun client disponible dans le répertoire Tosho.</p>
      ) : null}
      {scope === 'projets' ? (
        <p>
          <FinancialCalculationStatus calculation="projectMargin" label="Projets non raccordés" />{' '}
          Aucun projet fictif n’est attribué à un client. Les coûts du temps réel relèvent de
          S10-03.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{scope === 'clients' ? 'Client' : 'Projet'}</th>
              <th>Recettes HT</th>
              <th>Coûts HT</th>
              <th>Ce qu’il reste</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const margin = row.revenue - row.cost;
              return (
                <tr key={row.client.id}>
                  <th scope="row">
                    {demo ? <b className="profitability-proposal__rank">{index + 1}</b> : null}
                    {row.client.hasLogo ? (
                      <img
                        className="profitability-proposal__client-logo"
                        src={`/api/clients/${encodeURIComponent(row.client.id)}/logo?v=${row.client.version}`}
                        alt={row.client.displayName}
                        title={row.client.displayName}
                      />
                    ) : (
                      row.client.displayName
                    )}
                    {demo ? (
                      <i
                        className={`profitability-proposal__bar${margin < 0 ? ' is-negative' : ''}`}
                        style={{
                          width: `${(Math.abs(margin) / maximum) * 100}%`,
                        }}
                      />
                    ) : null}
                  </th>
                  <td>
                    {demo ? (
                      remunerationMoney(String(row.revenue))
                    ) : (
                      <FinancialCalculationStatus calculation="sources" />
                    )}
                  </td>
                  <td>
                    {demo ? (
                      remunerationMoney(String(row.cost))
                    ) : (
                      <FinancialCalculationStatus calculation="clientCosts" />
                    )}
                  </td>
                  <td className={demo ? (margin < 0 ? 'is-negative' : 'is-positive') : undefined}>
                    {demo ? (
                      remunerationMoney(String(margin))
                    ) : (
                      <FinancialCalculationStatus calculation="clientMargin" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p>
        Un coût non rattaché ne vaut pas zéro. Le classement réel signalera les informations à
        compléter.
      </p>
      <small>
        {demo
          ? 'Démonstration · noms et logos issus de Tosho · montants fictifs.'
          : 'Mode réel · identité client confirmée · calcul des recettes et des coûts encore à raccorder.'}
      </small>
    </article>
  );
}
